import { type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import * as React from "react";

import { cn } from "../../helpers";
import * as styles from "./button.styled";

type ButtonOwnProps = VariantProps<(typeof styles)["buttonVariants"]> & {
  LeftIcon?: React.ReactNode;
  RightIcon?: React.ReactNode;
};

export type ButtonProps<E extends React.ElementType = "button"> =
  ButtonOwnProps & {
    as?: E;
  } & Omit<React.ComponentPropsWithoutRef<E>, keyof ButtonOwnProps | "as">;

type ButtonComponent = <E extends React.ElementType = "button">(
  props: ButtonProps<E> & { ref?: React.Ref<Element> },
) => React.ReactNode;

const ButtonImpl = React.forwardRef(function Button<
  E extends React.ElementType = "button",
>(
  {
    className,
    variant,
    size,
    isLoading = false,
    isFullWidth = false,
    isFill = false,
    shape,
    as,
    LeftIcon,
    RightIcon,
    children,
    ...props
  }: ButtonProps<E>,
  ref: React.Ref<Element>,
) {
  const Comp = (as ?? "button") as React.ElementType;
  return isLoading ? (
    <Comp
      className={cn(
        styles.buttonVariants({
          variant,
          size,
          isLoading,
          isFullWidth,
          isFill,
          shape,
          className,
        }),
      )}
      ref={ref}
      {...(props as Record<string, unknown>)}
      disabled
    >
      <Loader2 className={styles.loaderIcon} />
    </Comp>
  ) : (
    <Comp
      className={cn(
        styles.buttonVariants({
          variant,
          size,
          isFullWidth,
          isFill,
          shape,
          className,
        }),
      )}
      ref={ref}
      {...(props as Record<string, unknown>)}
    >
      <div className={styles.wrapper}>
        {LeftIcon ? LeftIcon : null}
        {children}
        {RightIcon ? RightIcon : null}
      </div>
    </Comp>
  );
});

ButtonImpl.displayName = "Button";

const Button = ButtonImpl as unknown as ButtonComponent & {
  displayName?: string;
};

export { Button };
